/**
 * TTS Service - Text-to-Speech API client
 *
 * Calls the main worker's /api/tts (MeloTTS, AWS Polly fallback on error)
 * to generate speech audio and lip sync data (visemes) for mouth animation.
 */

/**
 * Lip sync data point - represents a mouth shape at a specific time
 */
export interface LipSyncPoint {
  /** Time in seconds from audio start */
  time: number
  /** Viseme index (0-14, Oculus standard) */
  value: number
  /** Optional duration for this viseme */
  duration?: number
}

/**
 * Response from the TTS API
 */
export interface TTSResponse {
  /** The text that was spoken (may be AI-generated response) */
  text: string
  /** URL to the audio file (can be data URL or HTTP URL) */
  audioUrl: string
  /** Lip sync viseme data for mouth animation */
  lipSync: LipSyncPoint[]
}

/**
 * Error thrown when TTS generation fails
 */
export class TTSError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = 'TTSError'
  }
}

/**
 * Generate speech audio and lip sync data from text
 *
 * @param text - The text to convert to speech
 * @param options - Optional configuration
 * @returns TTS response with audio URL and lip sync data
 * @throws TTSError if the request fails
 *
 * @example
 * ```ts
 * const { audioUrl, lipSync } = await generateSpeech("Hello, welcome to the assessment!")
 * // audioUrl: "https://..." or "data:audio/mp3;base64,..."
 * // lipSync: [{ time: 0, value: 10 }, { time: 0.1, value: 5 }, ...]
 * ```
 */
export async function generateSpeech(
  text: string,
  options?: {
    /** Optional session ID for conversation continuity */
    session?: string
    /** Request timeout in milliseconds (default: 30000) */
    timeout?: number
    /** Custom API base URL (overrides default) */
    apiUrl?: string
  }
): Promise<TTSResponse> {
  const timeout = options?.timeout ?? 30000

  // Validate input
  if (!text || typeof text !== 'string') {
    throw new TTSError('Text is required and must be a string')
  }

  const trimmedText = text.trim()
  if (trimmedText.length === 0) {
    throw new TTSError('Text cannot be empty')
  }

  if (trimmedText.length > 1000) {
    throw new TTSError('Text exceeds maximum length of 1000 characters')
  }

  // Create abort controller for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  // Single TTS endpoint: the worker uses MeloTTS and only falls back to
  // AWS Polly server-side if MeloTTS errors. No client-side fallback to
  // other engines, so the voice stays consistent.
  try {
    const response = await fetch(`${options?.apiUrl ?? ''}/api/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: trimmedText,
      }),
      signal: controller.signal,
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new TTSError(
        `TTS API error: ${response.status} - ${errorText}`,
        response.status
      )
    }

    const data = (await response.json()) as Record<string, unknown>
    return parseTtsResponse(data, trimmedText)
  } catch (error) {
    if (error instanceof TTSError) {
      throw error
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new TTSError(`TTS request timed out after ${timeout}ms`)
    }
    throw new TTSError(
      `TTS generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      error
    )
  } finally {
    clearTimeout(timeoutId)
  }
}

function parseTtsResponse(data: Record<string, unknown>, trimmedText: string): TTSResponse {
  if (!data || typeof data !== 'object') {
    throw new TTSError('Invalid response from TTS API: not an object')
  }

  let lipSyncData: unknown[] = []
  if (Array.isArray(data.lipSync)) {
    lipSyncData = data.lipSync
  } else if (
    data.lipSync &&
    typeof data.lipSync === 'object' &&
    Array.isArray((data.lipSync as Record<string, unknown>).visemes)
  ) {
    lipSyncData = (data.lipSync as Record<string, unknown>).visemes as unknown[]
  }

  const audioUrl = typeof data.audioUrl === 'string' ? data.audioUrl : ''
  if (!audioUrl) {
    throw new TTSError('TTS API returned no audio')
  }

  return {
    text: typeof data.text === 'string' ? data.text : trimmedText,
    audioUrl,
    lipSync: validateLipSync(lipSyncData),
  }
}

/**
 * Validate and normalize lip sync data
 */
function validateLipSync(data: unknown[]): LipSyncPoint[] {
  return data
    .filter((item): item is Record<string, unknown> => {
      return (
        item !== null &&
        typeof item === 'object' &&
        'time' in item &&
        'value' in item
      )
    })
    .map((item) => ({
      time: typeof item.time === 'number' ? item.time : 0,
      value: Math.max(0, Math.min(14, Math.round(Number(item.value) || 0))),
      duration: typeof item.duration === 'number' ? item.duration : undefined,
    }))
    .sort((a, b) => a.time - b.time)
}

/**
 * Check if the TTS service is available
 *
 * @returns true if the service responds successfully
 */
export async function checkTTSHealth(apiUrl?: string): Promise<boolean> {
  try {
    const response = await fetch(`${apiUrl ?? ''}/api/tts`, {
      method: 'OPTIONS',
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Viseme names for the 15 Oculus standard mouth shapes
 * Index corresponds to the `value` field in LipSyncPoint
 */
export const VISEME_NAMES = [
  'sil', // 0: Silence
  'PP', // 1: p, b, m
  'FF', // 2: f, v
  'TH', // 3: th
  'DD', // 4: d, t, n
  'kk', // 5: k, g
  'CH', // 6: ch, j, sh
  'SS', // 7: s, z
  'nn', // 8: n, l
  'RR', // 9: r
  'aa', // 10: a
  'E', // 11: e
  'I', // 12: i
  'O', // 13: o
  'U', // 14: u
] as const

export type VisemeName = (typeof VISEME_NAMES)[number]
